import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { checkIsEmpty, getSessionData } from "utils/common";

import { api } from "utils/api";
import {
  resolveSuccessWithFallback,
  shouldSuppressGenericSuccessToast,
} from "utils/toastPolicy";
import { parsePrivacyPolicyResponse } from "utils/privacyPolicyApi";

/** API may return `{ records: [] }`, `{ data: [] }`, or a bare array. */
function normalizeBankDetailsList(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.records)) return payload.records;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data && Array.isArray(payload.data.records)) return payload.data.records;
  return [];
}

/** Prefer API copy when non-empty; otherwise show a clear user-facing line. */
function messageOrFallback(apiMessage, fallback) {
  const s = apiMessage != null ? String(apiMessage).trim() : "";
  return s || fallback;
}

function apiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const m = error?.response?.data?.message;
  if (m != null && String(m).trim() !== "") return String(m);
  if (error?.request?.message) return String(error.request.message);
  if (error?.message) return String(error.message);
  return fallback;
}

function mapSegmentApiRows(data) {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    label: item.name,
    value: item.id,
    digitAfterPoint: item.digitAfterPoint,
    ...item,
  }));
}

function resolveSegmentUserId(arg) {
  if (arg == null) return null;
  if (typeof arg === "number" || typeof arg === "string") return arg;
  if (arg.userId != null) return arg.userId;
  const session = getSessionData() || {};
  return session.userId ?? session.id ?? session.UserId ?? null;
}

const initialState = {
  accountTypeOptionList: [],
  segmentsList: [],
  /** Cached segment list owner — refetch when session user changes. */
  segmentsListUserId: null,
  symbolList: [],
  scriptList: [],
  messageOptions: "success added",
  message: "",
  showMessage: false,
  varient: "success",
  openDeleteConfirmDialog: false,
  agentORSubBrokerList: [],
  expiryDatesBySymbolList: [],
  accountOptionList: [],
  partnerList: [],
  cashbookList: [],
  systemUserList: [],
  isNavbarHidden: false,
  editDataObjectCommon: {},
  commonAllUsers: [],
  focusState: false,
  rightsSessionList: [],
  notificationList: [],
  pushnotificationList: [],
  availableBalance: {},
  availableBalanceLoading: false,
  amountRangeList:[],
  whatappRecords:[],
  paymentGetway:[],
  rangeListLoading: false,
  bankDetails:[],
  isAddFund:false,
  addFundLoading:false,
  isBankUpdated:false,
  userBankLoading:false,
  iswithdrawFund:false,
  withdrawFundLoading:false,
  segmentListLoading: false,
  accountCommonListLoading: false,
  symbolListLoading: false,
  privacyPolicyData: [],
  privacyPolicyView: {
    title: "Privacy Policy & Terms",
    contentHtml: "",
    rules: [],
    updatedOn: null,
  },
  privacyPolicyLoading: false,
  privacyPolicyError: null,
  symbolListWithExpiry: [],
  isAcceptedRules: false,
  isAcceptedRulesLoader: false,
  payInOutListLoading:false,
  payInList:[],
  payOutList:[],
  totalPayInRecord:0,
  totalPayOutRecord:0,
  cashbookByRoleNameList:[],
};

export const getSegmentKeyWorkByValue = (selectObject) => {
  if (selectObject.value === 1) {
    return "fonse";
  } else if (selectObject.value === 2) {
    return "mcx";
  } else if (selectObject.value === 3) {
    return "fonse";
  } else if (selectObject.value === 4) {
    return "nsecd";
  }
};

export const isAllowShow = createAsyncThunk(
  "/isAllowShow/bool",
  (routeName, { dispatch, getState }) => {
    return new Promise((resolve, reject) => {
      let { rightsSessionList } = getState().common;
      let { roles } = getSessionData();
      if (roles[0] === "Super Admin") {
        resolve(true);
      } else {
        let arr =
          rightsSessionList &&
          rightsSessionList.filter((a) => a.routeName === routeName);
        if (arr.length > 0) {
          resolve(arr[0].isViewAllow);
        } else {
          resolve(false);
        }
      }
    });
  }
);

export const getSegmentOptionList = createAsyncThunk(
  "/segment/get",
  async (arg = {}, { getState }) => {
    const opts = typeof arg === "object" && arg !== null ? arg : {};
    const force = Boolean(opts.force);
    const userId = resolveSegmentUserId(opts);
    const { segmentsList = [], segmentsListUserId } = getState().common;
    const uidKey = userId != null ? String(userId) : "";

    if (!force && segmentsList.length > 0 && String(segmentsListUserId ?? "") === uidKey) {
      return segmentsList;
    }

    const url = userId
      ? `/master/segment/get-segments-by-user-id/${userId}`
      : "/master/segment/get-segments";

    const resData = await api.get(url);
    if (resData.status !== 200) throw resData;
    return mapSegmentApiRows(resData.data?.data);
  }
);

export const getSegmentOptionListByUser = createAsyncThunk(
  "/segment-by-user/get",
  async (userId) => {
    const resData = await api.get(`/master/segment/get-segments-by-user-id/${userId}`);
    if (resData.status !== 200) throw resData;
    return mapSegmentApiRows(resData.data?.data);
  }
);

export const getScriptList = createAsyncThunk(
  "/script/get",
  ({ id }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(
          `settings/symbol-expiry/get-symbol-expiry-symbols-by-segment/${id}`
        )
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getPrivacyPolicyData = createAsyncThunk("get-privacy-policy", async () => {
  const { data } = await api.get("common/get-privacy-policy-data");
  return parsePrivacyPolicyResponse(data);
});

let ajaxSymbolListRequest = null;
export const getSymbolList = createAsyncThunk(
  "/symbol/get",
  ({ id, optType }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      if (ajaxSymbolListRequest) ajaxSymbolListRequest.cancel("CANCEL");
      ajaxSymbolListRequest = axios.CancelToken.source();

      api
        .get(
          `/market-watch/get-symbols-by-segment/${id}${optType ? `?optType=${optType}` : ""}`,
          { cancelToken: ajaxSymbolListRequest.token }
        )
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getSymbolListWithExpiry = createAsyncThunk(
  "/symbol-with-exp/get",
  (segmentId, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`script/bhav-copy/Get-Script-Data-By-SegmentId/${segmentId}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const isRulesAccepted = createAsyncThunk(
  "/rules-accepted",
  (props) => {
    return new Promise((resolve, reject) => {
      axios
        .get('common/is-rules-accepted')
        .then(({ data }) => {
          if (data?.status === 200) {
            resolve(true);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getSymbolListForNse = createAsyncThunk(
  "/symbol/get-opt",
  ({ segmentId, IsQtySet }, { dispatch }) => {
    return new Promise((resolve, reject) => {
      if (ajaxSymbolListRequest) ajaxSymbolListRequest.cancel("CANCEL");
      ajaxSymbolListRequest = axios.CancelToken.source();
      axios
        .get(`market-watch/get-MW-symbols-for-nse/${segmentId}${IsQtySet ? `?IsQtySet=${IsQtySet}` : ""}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getAgentSubBroker = createAsyncThunk("/agentSubBroker", () => {
  return new Promise((resolve, reject) => {
    let { userId } = getSessionData();

    axios
      .get(`common/get-sub-brokers/${userId}`)
      .then(({ data }) => {
        let { status, data: dataArray } = data;
        if (status === 200 && dataArray && dataArray.length > 0) {
          resolve(dataArray);
        } else {
          reject(data);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getTokenScriptWiseLiveRateList = createAsyncThunk(
  "/script/get",
  ({ id }) => {
    return new Promise((resolve, reject) => {
      api
        .get(
          `settings/symbol-expiry/get-symbol-expiry-symbols-by-segment/${id}`
        )
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getAccountCommonList = createAsyncThunk(
  "/account/common",
  ({ userId, roleName }) => {
    return new Promise((resolve, reject) => {
      userId
        ? api
          .get(
            `common/get-account-names-by-authorityid-and-rolename/${userId}/${roleName}`
          )
          .then(({ data }) => {
            if (data.status === 200) {
              let { data: dataArray } = data;
              if (dataArray && dataArray.length > 0) {
                resolve({ dataArray, roleName });
              } else {
                reject(data);
              }
            } else {
              reject(data);
            }
          })
          .catch((error) => {
            reject(error);
          })
        : api
          .get(`common/get-account-names-by-rolename/${roleName}`)
          .then(({ data }) => {
            if (data.status === 200) {
              let { data: dataArray } = data;
              resolve({ dataArray, roleName });
            } else {
              reject(data);
            }
          })
          .catch((error) => {
            reject(error);
          });
    });
  }
);

export const getAccountTypeOption = createAsyncThunk("/accountType", () => {
  return new Promise((resolve, reject) => {
    api
      .get("common/get-account-types")
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          const newDataArray =
            data &&
            data.map((item, i) => {
              return { label: item.name, value: item.id };
            });
          resolve(newDataArray);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getCashbookByRoleName = createAsyncThunk("/cashbookbyrolename", () => {
  return new Promise((resolve, reject) => {
    axios
      .get("common/get-account-names-and-cashbook-by-rolename/cashbook")
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getNotificationList = createAsyncThunk("/getNotification", () => {
  return new Promise((resolve, reject) => {
    axios
      .get("common/get-today-notification-list")
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          if (data.length > 0) {
            data = data.reverse();
          }
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const markNotification = createAsyncThunk("/markNotification", () => {
  return new Promise((resolve, reject) => {
    axios
      .get("common/notifications/mark-as-all-read")
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const clearNotification = createAsyncThunk("/clearNotification", () => {
  return new Promise((resolve, reject) => {
    axios
      .get("common/notifications/clear-all-readed-notifications")
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getPushNotificationList = createAsyncThunk("/pushgetNotification", () => {
  return new Promise((resolve, reject) => {
    let { userId } = getSessionData();
    axios
      .get(`common/notifications/get-push-notifications-details/${userId}/1/50`)
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          if (data.records.length > 0) {
            data = data.records.reverse();
          }
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getAvailableBalance = createAsyncThunk("/availablebalance", () => {
  return new Promise((resolve, reject) => {
    api
      .get(`reports/ledger-report/get-ledger-balance-for-user`)
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const getRangeList = createAsyncThunk("/getRangeList", () => {
  return new Promise((resolve, reject) => {
    let { userId } = getSessionData();
    api
      .get(`finance/bank-detail/get-bank-details-for-user/${userId}/1/10000`)
      .then((resData) => {
        if (resData.status === 200) {
          let { data } = resData.data;
          resolve(data);
        } else {
          reject(resData);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
});

export const addFund = createAsyncThunk(
  "addFund",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { userId, roles } = getSessionData();
      let finalObject = data;
      finalObject.createdBy = userId;
      finalObject.id = 0;
      finalObject.userId = userId;
      finalObject.transactionId = data.transactionId;
      finalObject.amount = data.amount;
      finalObject.remark = data.remark;
      finalObject.payInImage = data.transactionFile;
      finalObject.status = 1;
      api
        .post("finance/Pay-InOut/save-pay-in-detail", finalObject)
        .then(({ data: dataRep }) => {
          if (dataRep.status == 200) {
            const line = resolveSuccessWithFallback(
              dataRep.message,
              "Pay-in request submitted successfully."
            );
            if (line) {
              dispatch(showMessage({ message: line, varient: "success" }));
            }
            resolve(dataRep);
          } else {
            dispatch(
              showMessage({
                message: messageOrFallback(
                  dataRep?.message,
                  "Pay-in request could not be submitted."
                ),
              })
            );
            reject(dataRep);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: apiErrorMessage(error) }));
          reject(error);
        });
    });
  }
);

export const leoPayIn = createAsyncThunk(
  "leoPayIn",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { amount } = data;
      api
        .get(`PaymentGateway/online-pay-in?amount=${amount}`)
        .then(({ data: dataRep }) => {
          if (dataRep.status == 200) {
            const line = resolveSuccessWithFallback(
              dataRep.message,
              "Continue in the payment window to complete your deposit."
            );
            if (line) {
              dispatch(showMessage({ message: line, varient: "success" }));
            }
            if (dataRep.data?.redirectUrl) {
              const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
              const width = isMobile ? window.innerWidth : 800;
              const height = isMobile ? window.innerHeight : 600;
              const left = isMobile ? 0 : (window.innerWidth - width) / 2;
              const top = isMobile ? 0 : (window.innerHeight - height) / 2;
              window.open(
                dataRep.data.redirectUrl,
                "LeoPayPayment",
                `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`
              );
            }
            resolve(dataRep);
          } else {
            dispatch(
              showMessage({
                message: messageOrFallback(
                  dataRep?.message,
                  "Online pay-in could not be started."
                ),
              })
            );
            reject(dataRep);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: apiErrorMessage(error) }));
          reject(error);
        });
    });
  }
);

export const withdrawFund = createAsyncThunk(
  "withdrawFund",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { userId, roles } = getSessionData();
      let finalObject = {};
      finalObject.bankUPI = data.upiId;
      finalObject.bankName = data.bankName;
      finalObject.bankHolderName = data.beneficiaryName;
      finalObject.bankAccountNumber = data.accountNumber;
      finalObject.bankIFSC = data.ifscCode;
      finalObject.createdBy = userId;
      finalObject.id = 0;
      finalObject.userId = userId;
      finalObject.status = 1;
      finalObject.amount = data.amount;
      api
        .post("finance/Pay-InOut/save-pay-out-detail", finalObject)
        .then(({ data: dataRep }) => {
          if (dataRep.status == 200) {
            const line = resolveSuccessWithFallback(
              dataRep.message,
              "Withdrawal request submitted successfully."
            );
            if (line) {
              dispatch(showMessage({ message: line, varient: "success" }));
            }
            resolve(dataRep.data ?? dataRep);
          } else {
            dispatch(
              showMessage({
                message: messageOrFallback(
                  dataRep?.message,
                  "Withdrawal request could not be submitted."
                ),
              })
            );
            reject(dataRep);
          }
        })
        .catch((error) => {
          dispatch(showMessage({ message: apiErrorMessage(error) }));
          reject(error);
        });
    });
  }
);

export const getPayInOutList = createAsyncThunk(
  "payInOutList",
  (data) => {
    return new Promise((resolve, reject) => {
      let { userId } = getSessionData();
      let payOut = data.payOut;
      let page = data.page;
      api
        .get(`finance/Pay-InOut/get-${payOut}-details/${userId}/${page}/10/0`)
        .then(({ data }) => {
          if (data.status == 200) {
            resolve({ data: data.data, type: payOut, page: page });
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getBankDetails = createAsyncThunk(
  "getBankDetails",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { userId, roles } = getSessionData();
      api
        .get(`finance/bank-detail/get-bank-details-for-users/${userId}/1/1000`)
        .then(({ data }) => {
          if (data.status === 200) {
            resolve(data.data);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const saveBankDetails = createAsyncThunk(
  "saveBankDetails",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { userId, roles } = getSessionData();
      let finalObject = {
        id: data.bankId ? data.bankId : 0,
        bankUPI: data.upiId,
        bankName: data.bankName,
        bankHolderName: data.beneficiaryName,
        bankAccountNumber: data.accountNumber,
        bankIFSC: data.ifscCode,
        bankUPIImage: "",
        createdBy: userId,
        endAmountRange: 0,
        isBankActive: true,
        startAmountRange: 0,
        bankAddress: "",
        deleted: false,
        userId: userId
      };
      const isNewBank = !data.bankId || Number(data.bankId) === 0;
      api
        .post("finance/bank-detail/save-bank-detail", finalObject)
        .then(({ data: dataRep }) => {
          if (dataRep.status == 200) {
            const fallback = isNewBank
              ? "Bank account added successfully."
              : "Bank details updated successfully.";
            const line = resolveSuccessWithFallback(dataRep.message, fallback);
            if (line) {
              dispatch(showMessage({ message: line, varient: "success" }));
            }
            dispatch(getBankDetails());
            resolve(dataRep.data ?? dataRep);
          } else {
            dispatch(
              showMessage({
                message: messageOrFallback(
                  dataRep?.message,
                  "Could not save bank details. Please try again."
                ),
              })
            );
            reject(dataRep);
          }
        })
        .catch((error) => {
          dispatch(
            showMessage({
              message: apiErrorMessage(
                error,
                "Could not save bank details. Please try again."
              ),
            })
          );
          reject(error);
        });
    });
  }
);

export const deleteUserBank = createAsyncThunk(
  "deleteUserBank",
  (bankId, { dispatch }) => {
    return new Promise((resolve, reject) => {
      api
        .delete(`finance/bank-detail/delete-bank-detail/${bankId}`)
        .then(({ data }) => {
          if (data.status == 200) {
            const line = resolveSuccessWithFallback(
              data.message,
              "Bank account removed from your saved list."
            );
            if (line) {
              dispatch(showMessage({ message: line, varient: "success" }));
            }
            dispatch(getBankDetails());
            resolve(data);
          } else {
            dispatch(
              showMessage({
                message: messageOrFallback(
                  data?.message,
                  "Could not remove bank account."
                ),
              })
            );
            reject(data);
          }
        })
        .catch((error) => {
          dispatch(
            showMessage({
              message: apiErrorMessage(
                error,
                "Could not remove bank account. Please try again."
              ),
            })
          );
          reject(error);
        });
    });
  }
);

export const getSymbolExpiryFromSymbol = createAsyncThunk(
  "/setting/symbol expiry/get",
  (data, { dispatch }) => {
    return new Promise((resolve, reject) => {
      let { id } = data;
      axios
        .get(`settings/symbol-expiry/get-expiry-dates-by-symbol/${id}`)
        .then(({ data }) => {
          let { status, data: dataArray } = data;
          if (status === 200 && dataArray && dataArray.length > 0) {
            resolve(dataArray);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getEditDataObjectCommon = createAsyncThunk(
  "/edit/data-object/get",
  (url, { dispatch }) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`${url}`)
        .then((res) => {
          let { status, data } = res.data;
          if (status === 200 && !checkIsEmpty(data)) {
            resolve(data);
          } else {
            reject(data);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const getRightsForSession = createAsyncThunk(
  "admin/getRightsForSession/list",
  (id) => {
    return new Promise((resolve, reject) => {
      axios
        .get(`settings/authorityRights/get-authorityRights-by-userid/${id}`)
        .then((resData) => {
          if (resData.status === 200) {
            let { data } = resData.data;
            sessionStorage.setItem(
              "TradeAuthoritySession",
              JSON.stringify(data)
            );
            resolve(data);
          } else {
            reject(resData);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
);

export const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    showMessage: (state, action) => {
      const p = action.payload || {};
      const msg = p.message != null ? String(p.message).trim() : "";
      // Old UI (`fxbazzar_frontend`) stores payload.varient as-is; omit → falsy → red "info" icon.
      // Accept common typo `variant` from some slices.
      const raw =
        p.varient !== undefined && p.varient !== null && p.varient !== ""
          ? p.varient
          : p.variant !== undefined && p.variant !== null && p.variant !== ""
            ? p.variant
            : "";
      const v = raw === "" ? "" : String(raw);
      // Many thunks omit `varient` but still pass API success boilerplate — suppress for all tones.
      if (shouldSuppressGenericSuccessToast(msg)) {
        state.showMessage = false;
        return;
      }
      state.message = msg;
      state.varient = v;
      state.showMessage = true;
    },
    hideMessage: (state, action) => {
      state.showMessage = false;
    },
    clearAddFundSuccess: (state) => {
      state.isAddFund = false;
    },
    setOpenDeleteConfirmDialog: (state, action) => {
      state.openDeleteConfirmDialog = action.payload;
    },
    setSymbolList: (state, action) => {
      state.symbolList = action.payload;
    },
    setAgentORSubBrokerList: (state, action) => {
      state.agentORSubBrokerList = action.payload;
    },
    setAccountOptionList: (state, action) => {
      state.accountOptionList = action.payload;
    },
    setIsNavbarHidden: (state, action) => {
      state.isNavbarHidden = action.payload;
    },
    setEditDataObjectCommon: (state, action) => {
      state.editDataObjectCommon = action.payload;
    },
    setFocusState: (state, action) => {
      state.focusState = action.payload;
    },
    setCommonAllUsers: (state, action) => {
      state.commonAllUsers = action.payload;
    },
    setSymbolListWithExpiry: (state, action) => {
      state.symbolListWithExpiry = action.payload;
    },
    setIsAcceptedRules: (state, action) => {
      state.isAcceptedRules = action.payload;
    },
    setIsBankUpdated: (state, action) => {
      state.isBankUpdated = action.payload;
    },
    clearSegmentsList: (state) => {
      state.segmentsList = [];
      state.segmentsListUserId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountCommonList.pending, (state) => {
        state.accountCommonListLoading = true;
      })
      .addCase(getAccountCommonList.rejected, (state) => {
        state.accountOptionList = [];
        state.accountCommonListLoading = false;
      })
      .addCase(getAccountCommonList.fulfilled, (state, action) => {
        state.accountCommonListLoading = false;
        if (action.payload.roleName === "User") {
          state.accountOptionList = action.payload.dataArray;
        } else if (
          action.payload.roleName === "Sub Broker" ||
          action.payload.roleName === "Broker"
        ) {
          state.agentORSubBrokerList = action.payload.dataArray;
        } else if (action.payload.roleName === "Partner") {
          state.partnerList = action.payload.dataArray;
        } else if (action.payload.roleName === "Cashbook") {
          state.cashbookList = action.payload.dataArray;
        } else if (action.payload.roleName === "System User") {
          state.systemUserList = action.payload.dataArray;
        }
        state.commonAllUsers = action.payload.dataArray;
      })
      // account type
      .addCase(getAccountTypeOption.rejected, (state) => {
        state.accountTypeOptionList = [];
      })
      .addCase(getAccountTypeOption.fulfilled, (state, action) => {
        state.accountTypeOptionList = action.payload;
      })
      .addCase(getCashbookByRoleName.pending, (state) => {
        state.cashbookByRoleNameList = [];
      })
      .addCase(getCashbookByRoleName.rejected, (state) => {
        state.cashbookByRoleNameList = [];
      })
      .addCase(getCashbookByRoleName.fulfilled, (state, action) => {
        state.cashbookByRoleNameList = action.payload;
      })
      // segment list
      .addCase(getSegmentOptionList.pending, (state) => {
        state.segmentListLoading = true;
      })
      .addCase(getSegmentOptionList.rejected, (state) => {
        state.segmentListLoading = false;
      })
      .addCase(getSegmentOptionList.fulfilled, (state, action) => {
        state.segmentsList = Array.isArray(action.payload) ? action.payload : [];
        const uid = resolveSegmentUserId(action.meta?.arg);
        if (uid != null) state.segmentsListUserId = String(uid);
        state.segmentListLoading = false;
      })
      .addCase(getSegmentOptionListByUser.pending, (state) => {
        state.segmentListLoading = true;
      })
      .addCase(getSegmentOptionListByUser.rejected, (state) => {
        state.segmentListLoading = false;
      })
      .addCase(getSegmentOptionListByUser.fulfilled, (state, action) => {
        state.segmentsList = Array.isArray(action.payload) ? action.payload : [];
        const uid = action.meta?.arg;
        if (uid != null) state.segmentsListUserId = String(uid);
        state.segmentListLoading = false;
      })
      // script list
      .addCase(getScriptList.pending, (state) => {
        state.scriptList = [];
      })
      .addCase(getScriptList.rejected, (state) => {
        state.scriptList = [];
      })
      .addCase(getScriptList.fulfilled, (state, action) => {
        state.scriptList = action.payload;
      })
      // rules accepted
      .addCase(isRulesAccepted.pending, (state) => {
        state.isAcceptedRulesLoader = true;
      })
      .addCase(isRulesAccepted.rejected, (state) => {
        state.isAcceptedRules = false;
        state.isAcceptedRulesLoader = false;
      })
      .addCase(isRulesAccepted.fulfilled, (state, action) => {
        state.isAcceptedRules = action?.payload || false;
        state.isAcceptedRulesLoader = false;
      })
      // privacy policy
      .addCase(getPrivacyPolicyData.pending, (state) => {
        state.privacyPolicyLoading = true;
        state.privacyPolicyError = null;
      })
      .addCase(getPrivacyPolicyData.rejected, (state, action) => {
        state.privacyPolicyData = [];
        state.privacyPolicyLoading = false;
        state.privacyPolicyError =
          action.error?.message || "Unable to load privacy policy. Please try again later.";
      })
      .addCase(getPrivacyPolicyData.fulfilled, (state, action) => {
        state.privacyPolicyView = action.payload || state.privacyPolicyView;
        state.privacyPolicyData = action.payload?.rules || [];
        state.privacyPolicyLoading = false;
        state.privacyPolicyError = null;
      })
      // symbol list
      .addCase(getSymbolList.pending, (state) => {
        state.symbolList = [];
        state.symbolListLoading = true;
      })
      .addCase(getSymbolList.rejected, (state) => {
        state.symbolList = [];
        state.symbolListLoading = false;
      })
      .addCase(getSymbolList.fulfilled, (state, action) => {
        state.symbolList = action.payload;
        state.symbolListLoading = false;
      })
      .addCase(getSymbolListWithExpiry.pending, (state) => {
        state.symbolListWithExpiry = [];
        state.symbolListLoading = true;
      })
      .addCase(getSymbolListWithExpiry.rejected, (state) => {
        state.symbolListWithExpiry = [];
        state.symbolListLoading = false;
      })
      .addCase(getSymbolListWithExpiry.fulfilled, (state, action) => {
        state.symbolListWithExpiry = action.payload;
        state.symbolListLoading = false;
      })
      .addCase(getSymbolListForNse.pending, (state) => {
        state.symbolList = [];
        state.symbolListLoading = true;
      })
      .addCase(getSymbolListForNse.rejected, (state) => {
        state.symbolList = [];
        state.symbolListLoading = false;
      })
      .addCase(getSymbolListForNse.fulfilled, (state, action) => {
        state.symbolList = action.payload;
        state.symbolListLoading = false;
      })
      // notifications
      .addCase(getNotificationList.pending, (state, action) => {
        state.notificationList = action.payload;
      })
      .addCase(getNotificationList.rejected, (state, action) => {
        state.notificationList = action.payload;
      })
      .addCase(getNotificationList.fulfilled, (state, action) => {
        state.notificationList = action.payload;
      })
      // push notifications
      .addCase(getPushNotificationList.pending, (state) => {
        state.pushnotificationList = [];
      })
      .addCase(getPushNotificationList.rejected, (state) => {
        state.pushnotificationList = [];
      })
      .addCase(getPushNotificationList.fulfilled, (state, action) => {
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          state.pushnotificationList = action.payload;
        } else {
          state.pushnotificationList = [];
        }
      })
      // available balance
      .addCase(getAvailableBalance.pending, (state) => {
        state.availableBalanceLoading = true;
      })
      .addCase(getAvailableBalance.rejected, (state) => {
        state.availableBalanceLoading = false;
        state.availableBalance = {};
      })
      .addCase(getAvailableBalance.fulfilled, (state, action) => {
        state.availableBalanceLoading = false;
        if (action.payload != null && typeof action.payload.balance !== "undefined") {
          state.availableBalance = action.payload;
        } else {
          state.availableBalance = {};
        }
      })
      // bank details (keep previous list while refetching — avoids empty flash in withdraw UI)
      .addCase(getBankDetails.pending, (state) => {})
      .addCase(getBankDetails.rejected, (state) => {
        state.bankDetails = [];
      })
      .addCase(getBankDetails.fulfilled, (state, action) => {
        state.bankDetails = normalizeBankDetailsList(action.payload);
      })
      .addCase(saveBankDetails.pending, (state) => {
        state.userBankLoading = true;
        state.isBankUpdated = false;
      })
      .addCase(saveBankDetails.rejected, (state) => {
        state.userBankLoading = false;
        state.isBankUpdated = false;
      })
      .addCase(saveBankDetails.fulfilled, (state) => {
        state.userBankLoading = false;
        state.isBankUpdated = true;
      })
      .addCase(deleteUserBank.pending, (state) => {
        state.userBankLoading = true;
        state.isBankUpdated = false;
      })
      .addCase(deleteUserBank.rejected, (state) => {
        state.userBankLoading = false;
        state.isBankUpdated = false;
      })
      .addCase(deleteUserBank.fulfilled, (state) => {
        state.userBankLoading = false;
        state.isBankUpdated = true;
      })
      // amount range (do not clear lists on pending — avoids blank UI while refetching)
      .addCase(getRangeList.pending, (state) => {
        state.rangeListLoading = true;
      })
      .addCase(getRangeList.rejected, (state) => {
        state.rangeListLoading = false;
        state.amountRangeList = [];
        state.whatappRecords = [];
        state.paymentGetway = [];
      })
      .addCase(getRangeList.fulfilled, (state, action) => {
        state.rangeListLoading = false;
        if (
          Array.isArray(action.payload.records) &&
          action.payload.records.length > 0
        ) {
          state.amountRangeList = action.payload.records;
        } else {
          state.amountRangeList = [];
        }
        if (
          Array.isArray(action.payload.whatappRecord) &&
          action.payload.whatappRecord.length > 0
        ) {
          state.whatappRecords = action.payload.whatappRecord;
        } else {
          state.whatappRecords = [];
        }
        if (
          Array.isArray(action.payload.paymentGetway) &&
          action.payload.paymentGetway.length > 0
        ) {
          state.paymentGetway = action.payload.paymentGetway;
        } else {
          state.paymentGetway = [];
        }
      })
      // add fund
      .addCase(addFund.pending, (state) => {
        state.addFundLoading = true;
        state.isAddFund = false;
      })
      .addCase(addFund.rejected, (state) => {
        state.addFundLoading = false;
        state.isAddFund = false;
      })
      .addCase(addFund.fulfilled, (state) => {
        state.addFundLoading = false;
        state.isAddFund = true;
      })
      .addCase(leoPayIn.pending, (state) => {
        state.addFundLoading = true;
        state.isAddFund = false;
      })
      .addCase(leoPayIn.rejected, (state) => {
        state.addFundLoading = false;
        state.isAddFund = false;
      })
      .addCase(leoPayIn.fulfilled, (state) => {
        state.addFundLoading = false;
        state.isAddFund = true;
      })
      // withdraw fund
      .addCase(withdrawFund.pending, (state) => {
        state.withdrawFundLoading = true;
        state.iswithdrawFund = false;
      })
      .addCase(withdrawFund.rejected, (state) => {
        state.withdrawFundLoading = false;
        state.iswithdrawFund = false;
      })
      .addCase(withdrawFund.fulfilled, (state) => {
        state.withdrawFundLoading = false;
        state.iswithdrawFund = true;
      })
      // pay in/out list
      .addCase(getPayInOutList.pending, (state) => {
        state.payInOutListLoading = true;
      })
      .addCase(getPayInOutList.rejected, (state) => {
        state.payInOutList = [];
        state.payInOutListLoading = false;
      })
      .addCase(getPayInOutList.fulfilled, (state, action) => {
        const records = action.payload?.data?.records ?? [];
        const totalRecords = action.payload?.data?.totalRecords ?? 0;
        if (records.length > 0) {
          if (action.payload.type == "pay-in") {
            state.payInList =
              action.payload.page === 1
                ? records
                : [...state.payInList, ...records];
            state.totalPayInRecord = totalRecords;
          } else {
            state.payOutList =
              action.payload.page === 1
                ? records
                : [...state.payOutList, ...records];
            state.totalPayOutRecord = totalRecords;
          }
        } else if (action.payload.page === 1) {
          if (action.payload.type == "pay-in") {
            state.payInList = [];
            state.totalPayInRecord = totalRecords;
          } else {
            state.payOutList = [];
            state.totalPayOutRecord = totalRecords;
          }
        }
        state.payInOutListLoading = false;
      })
      // symbol expiry
      .addCase(getSymbolExpiryFromSymbol.pending, (state) => {
        state.expiryDatesBySymbolList = [];
      })
      .addCase(getSymbolExpiryFromSymbol.rejected, (state) => {
        state.expiryDatesBySymbolList = [];
      })
      .addCase(getSymbolExpiryFromSymbol.fulfilled, (state, action) => {
        state.expiryDatesBySymbolList = action.payload;
      })
      // edit data object
      .addCase(getEditDataObjectCommon.rejected, (state) => {
        state.editDataObjectCommon = [];
      })
      .addCase(getEditDataObjectCommon.fulfilled, (state, action) => {
        state.editDataObjectCommon = action.payload;
      })
      // rights session
      .addCase(getRightsForSession.rejected, (state) => {
        state.rightsSessionList = [];
      })
      .addCase(getRightsForSession.fulfilled, (state, action) => {
        state.rightsSessionList = action.payload;
      });
  },
});

export const {
  showMessage,
  hideMessage,
  clearAddFundSuccess,
  setOpenDeleteConfirmDialog,
  setSymbolList,
  setAgentORSubBrokerList,
  setAccountOptionList,
  setIsNavbarHidden,
  setEditDataObjectCommon,
  setFocusState,
  setCommonAllUsers,
  setSymbolListWithExpiry,
  setIsAcceptedRules,
  setIsBankUpdated,
  clearSegmentsList,
} = commonSlice.actions;

export default commonSlice.reducer;
